import React from 'react';
import { motion } from 'framer-motion';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface MarketSkill {
  name: string;
  level: string;
}

interface UserSkill {
  skill_name: string;
  proficiency_level: string;
}

interface SkillVisualizationProps {
  marketSkills: MarketSkill[];
  userSkills: UserSkill[];
}

const SkillVisualization: React.FC<SkillVisualizationProps> = ({ marketSkills, userSkills }) => {
  const proficiencyLevels = {
    'beginner': 25,
    'intermediate': 50,
    'advanced': 75,
    'expert': 100
  };

  const generateGrowthData = (currentLevel: number, targetLevel: number) => {
    // Create more realistic growth curve with acceleration and plateaus
    return Array.from({ length: 6 }, (_, i) => {
      let progress;
      const x = i / 5; // Normalize to 0-1 range
      
      if (currentLevel < targetLevel) {
        // S-curve growth for improvement
        progress = currentLevel + (targetLevel - currentLevel) * (1 / (1 + Math.exp(-10 * (x - 0.5))));
        // Add some realistic variation
        progress += Math.sin(x * Math.PI) * 3;
      } else {
        // Decay curve for maintaining high level
        progress = targetLevel + (currentLevel - targetLevel) * Math.exp(-3 * x);
        // Add slight variation
        progress += Math.sin(x * Math.PI) * 2;
      }
      
      // Ensure progress stays within bounds
      progress = Math.max(currentLevel, Math.min(targetLevel, progress));
      
      return {
        month: `M${i}`,
        progress: Math.round(progress),
        target: targetLevel,
        baseline: currentLevel
      };
    });
  };

  const skill = marketSkills[0];
  const userSkill = userSkills.find(us => us.skill_name.toLowerCase() === skill.name.toLowerCase());
  const currentLevel = userSkill ? proficiencyLevels[userSkill.proficiency_level.toLowerCase()] || 0 : 0;
  const targetLevel = proficiencyLevels[skill.level.toLowerCase()] || 100;
  const data = generateGrowthData(currentLevel, targetLevel);

  const getGrowthAnalysis = (current: number, target: number, progress: number) => {
    const gap = target - current;
    const progressMade = progress - current;
    const remainingProgress = target - progress;
    
    if (gap <= 0) return "You're already at or above the target level. Focus on maintaining your expertise and staying updated with the latest developments.";
    
    if (progressMade < gap * 0.2) {
      return "You're in the early stages of skill development. Focus on foundational learning and consistent practice.";
    } else if (progressMade < gap * 0.5) {
      return "You're making steady progress. Continue with structured learning and start applying skills in real projects.";
    } else if (progressMade < gap * 0.8) {
      return "You're approaching proficiency. Focus on advanced concepts and practical applications.";
    } else {
      return "You're very close to your target level. Fine-tune your skills and focus on mastery.";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white p-6 rounded-xl shadow-md"
    >
      <h3 className="text-xl font-semibold text-gray-900 mb-6">Growth Trajectory</h3>
      
      <div className="h-64 mb-6">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis domain={[0, 100]} />
            <Tooltip />
            <Line 
              type="monotone" 
              dataKey="progress" 
              stroke="#3B82F6" 
              strokeWidth={2}
              dot={{ r: 4 }}
              activeDot={{ r: 6 }}
            />
            <Line 
              type="monotone" 
              dataKey="target" 
              stroke="#10B981" 
              strokeDasharray="5 5"
              strokeWidth={2}
              dot={false}
            />
            <Line 
              type="monotone" 
              dataKey="baseline" 
              stroke="#6B7280" 
              strokeDasharray="5 5"
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-3 gap-4 text-center mb-6">
        <div className="bg-gray-50 p-4 rounded-lg">
          <div className="text-sm font-medium text-gray-500">Current Level</div>
          <div className="text-xl font-semibold text-gray-900">{currentLevel}%</div>
        </div>
        <div className="bg-primary-50 p-4 rounded-lg">
          <div className="text-sm font-medium text-primary-600">Progress</div>
          <div className="text-xl font-semibold text-primary-600">
            {data[data.length - 1].progress}%
          </div>
        </div>
        <div className="bg-green-50 p-4 rounded-lg">
          <div className="text-sm font-medium text-green-600">Target Level</div>
          <div className="text-xl font-semibold text-green-600">{targetLevel}%</div>
        </div>
      </div>

      <div className="p-4 bg-gray-50 rounded-lg">
        <h4 className="font-medium text-gray-900 mb-2">Growth Analysis</h4>
        <p className="text-gray-600 text-sm leading-relaxed">
          {getGrowthAnalysis(currentLevel, targetLevel, data[data.length - 1].progress)}
          {targetLevel - data[data.length - 1].progress > 0 && (
            ` You're ${Math.round((data[data.length - 1].progress - currentLevel) / (targetLevel - currentLevel) * 100)}% of the way to your target level.`
          )}
        </p>
      </div>
    </motion.div>
  );
};

export default SkillVisualization;